import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { grey } from "@mui/material/colors";

import { useAppSelector } from "app/hooks";
import {
  MY_COMPLETED_TASKS,
  TASKS_ASSIGNED_TO_ME,
  TASKS_CREATED_BY_ME,
  TASK_STATUS_COMPLETED,
  TASK_STATUS_IN_PROGRESS,
  TASK_STATUS_TODO
} from "constants/staff-tasks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useQueryStaffTasksCounterQuery } from "services/staff-tasks";

import useStaffTasksV2 from "../hooks/use-staff-tasks-v2.hook";
import StaffTasksDrawerHeader from "./staff-tasks-drawer-header.component";
import TaskTabLabel from "./tasks-tab/task-tab-label.component";
import TasksTab from "./tasks-tab/tasks-tab.component";

const tabSx = { fontSize: "15px", px: 0 };

export default function StaffTasksDrawerContent() {
  const currentUser = useAppSelector(selectCurrentUser);
  const {
    taskDrawerMode,
    isPatientViewMode,
    patientId,
    taskViewType,
    setTaskViewType,
    includeCreatedCompleted,
    setIncludeCreatedCompleted
  } = useStaffTasksV2();

  const tasksSkipCondition = !currentUser.id || currentUser.id === -1;

  const { data: tasksAssignedToMeCount = 0 } = useQueryStaffTasksCounterQuery(
    {
      assignee: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_complete: false,
      status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    {
      skip: tasksSkipCondition
    }
  );

  const { data: tasksCreatedByMeCount = 0 } = useQueryStaffTasksCounterQuery(
    {
      created_by: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      status: includeCreatedCompleted
        ? [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS, TASK_STATUS_COMPLETED]
        : [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    {
      skip: taskDrawerMode !== "view" || tasksSkipCondition
    }
  );

  const { data: myCompletedTasksCount = 0 } = useQueryStaffTasksCounterQuery(
    {
      assignee: currentUser.id,
      patient: isPatientViewMode ? patientId : undefined,
      is_active: true,
      is_complete: true
    },
    {
      skip: taskDrawerMode !== "view" || tasksSkipCondition
    }
  );

  return (
    <>
      <StaffTasksDrawerHeader />

      <Tabs
        centered
        variant="fullWidth"
        value={taskViewType}
        onChange={(_, value) => setTaskViewType(value)}
      >
        <Tab
          value={TASKS_ASSIGNED_TO_ME}
          label={
            <TaskTabLabel
              count={tasksAssignedToMeCount}
              isActive={taskViewType === TASKS_ASSIGNED_TO_ME}
            >
              Assigned To Me
            </TaskTabLabel>
          }
          sx={tabSx}
        />

        <Tab
          value={TASKS_CREATED_BY_ME}
          label={
            <TaskTabLabel
              count={tasksCreatedByMeCount}
              badgeBgColor={grey[300]}
              badgeColor={grey[700]}
              isActive={taskViewType === TASKS_CREATED_BY_ME}
            >
              Created By Me
            </TaskTabLabel>
          }
          sx={tabSx}
        />

        <Tab
          value={MY_COMPLETED_TASKS}
          label={
            <TaskTabLabel
              count={myCompletedTasksCount}
              badgeBgColor={grey[300]}
              badgeColor={grey[700]}
              isActive={taskViewType === MY_COMPLETED_TASKS}
            >
              Completed
            </TaskTabLabel>
          }
          sx={tabSx}
        />
      </Tabs>
      {taskViewType === TASKS_CREATED_BY_ME && (
        <Box sx={{ px: 2, pt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeCreatedCompleted}
                onChange={(e) => setIncludeCreatedCompleted(e.target.checked)}
              />
            }
            label="Include Completed"
            componentsProps={{ typography: { variant: "body2", color: "text.secondary" } }}
          />
        </Box>
      )}
      <TasksTab taskViewType={taskViewType} key={`${taskViewType}-${includeCreatedCompleted}`} />
    </>
  );
}
